import { useState, useEffect, useRef, useCallback } from "react";
import {
    Car, MapPin, Plus, Loader2, AlertTriangle, CheckCircle2, KeyRound,
    Undo2, FileText, PenLine, Mail, X, CircleDot,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../lib/pdfPreview";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { Textarea } from "../ui/textarea";

const STATUS_META = {
    verfuegbar: { label: "Verfügbar", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "text-emerald-500" },
    reserviert: { label: "Reserviert", cls: "bg-amber-100 text-amber-800 border-amber-300", dot: "text-amber-500" },
    vermietet: { label: "Vermietet", cls: "bg-blue-100 text-blue-800 border-blue-300", dot: "text-blue-500" },
};
const fmtEur = (n) => Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

// ---------------------------------------------------------------------------
//  Signatur-Pad (Touch/Maus) — für Tablet-Unterschrift
// ---------------------------------------------------------------------------
function SignaturePad({ onSave, onCancel }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const [hasInk, setHasInk] = useState(false);

    const pos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#111827"; ctx.stroke(); setHasInk(true); };
    const end = () => { drawing.current = false; };
    const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };

    return (
        <div data-testid="signature-pad">
            <canvas
                ref={canvasRef} width={520} height={180}
                className="w-full border-2 border-dashed border-gray-300 rounded-sm bg-white touch-none cursor-crosshair"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                data-testid="signature-canvas"
            />
            <div className="flex justify-between mt-2">
                <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-xs" data-testid="signature-clear">Löschen</Button>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onCancel} className="text-xs">Abbrechen</Button>
                    <Button type="button" size="sm" disabled={!hasInk} onClick={() => onSave(canvasRef.current.toDataURL("image/png"))}
                            className="text-xs bg-[#111827] text-white" data-testid="signature-save">Unterschrift übernehmen</Button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
//  Kundendaten-Formular (wiederverwendbar)
// ---------------------------------------------------------------------------
function CustomerFields({ c, set, withLicence }) {
    const f = (k, label, ph, req) => (
        <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">{label}{req && <span className="text-red-500"> *</span>}</Label>
            <Input value={c[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="rounded-sm h-9 mt-1" data-testid={`rental-cust-${k}`} />
        </div>
    );
    return (
        <div className="grid grid-cols-2 gap-2">
            {f("first_name", "Vorname", "Max", withLicence)}
            {f("last_name", "Nachname", "Mustermann", withLicence)}
            <div className="col-span-2 grid grid-cols-3 gap-2">
                <div className="col-span-2">{f("street", "Straße", "Musterweg")}</div>
                {f("house_number", "Nr.", "12")}
            </div>
            <div className="grid grid-cols-3 gap-2 col-span-2">
                {f("postal_code", "PLZ", "13585")}
                <div className="col-span-2">{f("city", "Ort", "Berlin")}</div>
            </div>
            {f("phone", "Handynummer", "0176 …", withLicence)}
            {f("email", "E-Mail", "max@…", withLicence)}
            {withLicence && (
                <>
                    {f("id_card_number", "Ausweisnummer", "L01X…", true)}
                    {f("drivers_license_number", "Führerscheinnummer", "FS…", true)}
                    {f("dl_issue_date", "Ausstellungsdatum", "TT.MM.JJJJ")}
                    {f("dl_authority", "ausstellende Behörde", "Bürgeramt …")}
                    {f("dl_class", "Führerscheinklasse", "A, B …")}
                </>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
//  Modal-Shell
// ---------------------------------------------------------------------------
function Modal({ title, onClose, children, wide, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
            <div className={`bg-white rounded-sm shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} my-8`}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
                    <h3 className="font-display font-black text-lg">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="rental-modal-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

// ===========================================================================
export default function RentalVehiclesTab() {
    const [groups, setGroups] = useState([]);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    // Modal-States
    const [reserveFor, setReserveFor] = useState(null);
    const [pickupBooking, setPickupBooking] = useState(null);
    const [returnBooking, setReturnBooking] = useState(null);
    const [contractBooking, setContractBooking] = useState(null);

    const load = useCallback(async () => {
        try {
            const [v, a] = await Promise.all([
                api.get("/dealer/rental-vehicles"),
                api.get("/dealer/rental-articles"),
            ]);
            setGroups(v.data.groups || []);
            setArticles(a.data.items || []);
        } catch { toast.error("Ersatzwagen konnten nicht geladen werden"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [v, a] = await Promise.all([
                    api.get("/dealer/rental-vehicles"),
                    api.get("/dealer/rental-articles"),
                ]);
                if (!active) return;
                setGroups(v.data.groups || []);
                setArticles(a.data.items || []);
            } catch { /* toast on reload only */ }
            finally { if (active) setLoading(false); }
        })();
        return () => { active = false; };
    }, []);

    const openPickup = async (vehicle) => {
        try {
            const { data } = await api.get(`/dealer/rental-bookings/${vehicle.active_booking_id}`);
            setPickupBooking({ ...data, prefill_km: vehicle.prefill_pickup_km });
        } catch { toast.error("Buchung nicht gefunden"); }
    };
    const openReturn = async (vehicle) => {
        try {
            const { data } = await api.get(`/dealer/rental-bookings/${vehicle.active_booking_id}`);
            setReturnBooking(data);
        } catch { toast.error("Buchung nicht gefunden"); }
    };
    const openContract = async (bookingId) => {
        try {
            const { data } = await api.get(`/dealer/rental-bookings/${bookingId}`);
            setContractBooking(data);
        } catch { toast.error("Buchung nicht gefunden"); }
    };

    if (loading) return <div className="text-center py-16 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

    const noArticles = articles.length === 0;
    const totalVehicles = groups.reduce((s, g) => s + g.vehicles.length, 0);

    return (
        <div className="space-y-6" data-testid="rental-vehicles-tab">
            <div className="bg-teal-50 border border-teal-200 rounded-sm p-3 flex items-start gap-2">
                <Car className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
                <p className="text-xs text-teal-900">
                    Zugelassene <strong>Vorführfahrzeuge</strong> mit Nutzung &bdquo;Miet/Werkstatt&ldquo; (aus der Disposition), je hinterlegtem Standort.
                    Buchung über einen WMS-Artikel der Kategorie <strong>&bdquo;Miet/Ersatzfahrzeug&ldquo;</strong> (= Tagespreis).
                </p>
            </div>

            {noArticles && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="rental-no-articles">
                    Noch kein Miet-Artikel angelegt. Lege in der <strong>Warenwirtschaft</strong> einen Artikel der Kategorie &bdquo;Miet/Ersatzfahrzeug&ldquo; mit Tagespreis (Brutto-VK) an, um Fahrzeuge buchen zu können.
                </div>
            )}

            {totalVehicles === 0 ? (
                <div className="text-center py-16 text-gray-400" data-testid="rental-empty">
                    <Car className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    Keine Miet-/Werkstattersatzfahrzeuge. Markiere in der Disposition ein zugelassenes Vorführfahrzeug als &bdquo;Miet/Werkstatt&ldquo; und wähle einen Standort.
                </div>
            ) : groups.map((g) => (
                <div key={g.site_code} data-testid={`rental-site-${g.site_code}`}>
                    <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-[#111827]" />
                        <h3 className="font-display font-black">{g.site_name}</h3>
                        <span className="text-xs text-gray-400">({g.vehicles.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {g.vehicles.map((v) => {
                            const meta = STATUS_META[v.status] || STATUS_META.verfuegbar;
                            return (
                                <div key={v.id} className={`bg-white border rounded-sm p-3 ${v.overdue ? "border-red-400 ring-1 ring-red-300" : "border-gray-200"}`} data-testid={`rental-vehicle-${v.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm truncate">{v.manufacturer} {v.model}</div>
                                            <div className="text-[11px] text-gray-500 font-mono-num">{v.displacement ? `${v.displacement} ccm · ` : ""}{v.license_plate || "ohne Kennz."}{v.color ? ` · ${v.color}` : ""}</div>
                                        </div>
                                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border inline-flex items-center gap-1 shrink-0 ${meta.cls}`} data-testid={`rental-status-${v.id}`}>
                                            <CircleDot className={`w-3 h-3 ${meta.dot}`} /> {meta.label}
                                        </span>
                                    </div>
                                    {(v.active_from || v.active_to) && (
                                        <div className="mt-2 text-[11px] text-gray-600">
                                            {fmtDate(v.active_from)} – {fmtDate(v.active_to)}
                                            {v.active_customer_name ? ` · ${v.active_customer_name}` : ""}
                                        </div>
                                    )}
                                    {v.overdue && (
                                        <div className="mt-1 text-[11px] font-bold text-red-700 inline-flex items-center gap-1" data-testid={`rental-overdue-${v.id}`}>
                                            <AlertTriangle className="w-3.5 h-3.5" /> Rückgabetermin überschritten!
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {v.status === "verfuegbar" && (
                                            <Button size="sm" disabled={noArticles} onClick={() => setReserveFor(v)} className="h-7 text-[11px] rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold" data-testid={`rental-book-btn-${v.id}`}>
                                                <Plus className="w-3 h-3 mr-1" /> Reservieren / Vermieten
                                            </Button>
                                        )}
                                        {v.status === "reserviert" && (
                                            <Button size="sm" onClick={() => openPickup(v)} className="h-7 text-[11px] rounded-sm bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid={`rental-pickup-btn-${v.id}`}>
                                                <KeyRound className="w-3 h-3 mr-1" /> Abholung
                                            </Button>
                                        )}
                                        {v.status === "vermietet" && (
                                            <Button size="sm" onClick={() => openReturn(v)} className="h-7 text-[11px] rounded-sm bg-teal-600 hover:bg-teal-700 text-white font-bold" data-testid={`rental-return-btn-${v.id}`}>
                                                <Undo2 className="w-3 h-3 mr-1" /> Rückgabe
                                            </Button>
                                        )}
                                        {v.active_booking_id && (
                                            <Button size="sm" variant="outline" onClick={() => openContract(v.active_booking_id)} className="h-7 text-[11px] rounded-sm" data-testid={`rental-contract-btn-${v.id}`}>
                                                <FileText className="w-3 h-3 mr-1" /> Vertrag
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {reserveFor && <ReserveModal vehicle={reserveFor} articles={articles} busy={busy} setBusy={setBusy} onClose={() => setReserveFor(null)} onDone={() => { setReserveFor(null); load(); }} />}
            {pickupBooking && <PickupModal booking={pickupBooking} busy={busy} setBusy={setBusy} onClose={() => setPickupBooking(null)} onDone={() => { setPickupBooking(null); load(); }} onContract={(id) => { setPickupBooking(null); openContract(id); }} />}
            {returnBooking && <ReturnModal booking={returnBooking} busy={busy} setBusy={setBusy} onClose={() => setReturnBooking(null)} onDone={() => { setReturnBooking(null); load(); }} />}
            {contractBooking && <ContractModal booking={contractBooking} onClose={() => setContractBooking(null)} onReload={load} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
function ReserveModal({ vehicle, articles, busy, setBusy, onClose, onDone }) {
    const [articleId, setArticleId] = useState(articles[0]?.id || "");
    const [start, setStart] = useState(todayISO());
    const [end, setEnd] = useState(addDaysISO(1));
    const [cust, setCust] = useState({});
    const art = articles.find((a) => a.id === articleId);
    const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);
    const total = (art?.price_per_day_gross || 0) * days;

    const submit = async () => {
        if (!articleId) { toast.error("Bitte Miet-Artikel wählen"); return; }
        setBusy(true);
        try {
            await api.post("/dealer/rental-bookings", {
                vehicle_listing_id: vehicle.id, article_id: articleId,
                start_date: start, end_date: end,
                customer: Object.keys(cust).length ? cust : null,
            });
            toast.success("Reservierung angelegt");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Reservieren"); }
        finally { setBusy(false); }
    };

    return (
        <Modal title={`Reservieren — ${vehicle.manufacturer} ${vehicle.model}`} onClose={onClose} wide testid="rental-reserve-modal">
            <div className="space-y-4">
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-gray-500">Miet-Artikel (Tagespreis)</Label>
                    <Select value={articleId} onValueChange={setArticleId}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="rental-article-select"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
                        <SelectContent>
                            {articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} — {fmtEur(a.price_per_day_gross)}/Tag</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Von (Ausgabe)</Label>
                        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm mt-1" data-testid="rental-start-date" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Bis (Rückgabe)</Label>
                        <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="rounded-sm mt-1" data-testid="rental-end-date" />
                    </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2 text-sm flex justify-between" data-testid="rental-total-preview">
                    <span className="text-gray-600">{days} Tag(e) × {fmtEur(art?.price_per_day_gross)}</span>
                    <span className="font-bold">{fmtEur(total)}</span>
                </div>
                <details className="text-sm">
                    <summary className="cursor-pointer font-bold text-gray-700">Mieter-Daten (optional, kann bei Abholung ergänzt werden)</summary>
                    <div className="mt-2"><CustomerFields c={cust} set={(k, v) => setCust((p) => ({ ...p, [k]: v }))} withLicence={false} /></div>
                </details>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#111827] text-white font-bold" data-testid="rental-reserve-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reservieren"}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
function PickupModal({ booking, busy, setBusy, onClose, onDone, onContract }) {
    const [cust, setCust] = useState(booking.customer || {});
    const [km, setKm] = useState(booking.prefill_km != null ? String(booking.prefill_km) : "");
    const [fuel, setFuel] = useState("voll");
    const [opts, setOpts] = useState({ has_winter_tires: false, has_summer_tires: false, papers_given: true, keys_given: true });

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/dealer/rental-bookings/${booking.id}/pickup`, {
                customer: cust,
                pickup_km: km ? parseFloat(km) : null,
                pickup_fuel: fuel,
                ...opts,
            });
            toast.success("Fahrzeug ausgegeben — Status: vermietet");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Abholung fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <Modal title="Abholung / Übergabe" onClose={onClose} wide testid="rental-pickup-modal">
            <div className="space-y-4">
                <p className="text-xs text-gray-500">Bitte Mieter-Daten prüfen/ergänzen. Ausweis- und Führerscheinnummer sind Pflicht.</p>
                <CustomerFields c={cust} set={(k, v) => setCust((p) => ({ ...p, [k]: v }))} withLicence />
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Km bei Ausgabe {booking.prefill_km != null && <span className="text-emerald-600">(aus letzter Rückgabe)</span>}</Label>
                        <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} className="rounded-sm mt-1 h-9" data-testid="rental-pickup-km" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Tankinhalt</Label>
                        <Input value={fuel} onChange={(e) => setFuel(e.target.value)} placeholder="voll, 1/2 …" className="rounded-sm mt-1 h-9" data-testid="rental-pickup-fuel" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs">
                    {[["has_winter_tires", "Winterräder"], ["has_summer_tires", "Sommerräder"], ["papers_given", "Kfz-Papiere"], ["keys_given", "Schlüssel"]].map(([k, l]) => (
                        <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={opts[k]} onChange={(e) => setOpts((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`rental-opt-${k}`} /> {l}
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-blue-600 text-white font-bold" data-testid="rental-pickup-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ausgeben & vermieten"}
                    </Button>
                </div>
                <button onClick={() => onContract(booking.id)} className="text-xs text-gray-500 underline" data-testid="rental-pickup-contract">Mietvertrag öffnen →</button>
            </div>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
function ReturnModal({ booking, busy, setBusy, onClose, onDone }) {
    const [km, setKm] = useState("");
    const [fuel, setFuel] = useState("voll");
    const [defects, setDefects] = useState("");

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/dealer/rental-bookings/${booking.id}/return`, {
                return_km: km ? parseFloat(km) : null, return_fuel: fuel, return_defects: defects || null,
            });
            toast.success("Rückgabe erfasst");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Rückgabe fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <Modal title="Rückgabe" onClose={onClose} testid="rental-return-modal">
            <div className="space-y-4">
                <div className="text-xs text-gray-500">Km bei Ausgabe: <strong>{booking.pickup_km ?? "—"}</strong></div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Km-Stand bei Rückgabe</Label>
                        <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} className="rounded-sm mt-1 h-9" data-testid="rental-return-km" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-gray-500">Tankinhalt</Label>
                        <Input value={fuel} onChange={(e) => setFuel(e.target.value)} className="rounded-sm mt-1 h-9" data-testid="rental-return-fuel" />
                    </div>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-wider text-gray-500">Festgestellte Mängel</Label>
                    <Textarea value={defects} onChange={(e) => setDefects(e.target.value)} rows={3} className="rounded-sm mt-1" data-testid="rental-return-defects" />
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm">Abbrechen</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-sm bg-teal-600 text-white font-bold" data-testid="rental-return-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rückgabe abschließen"}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
function ContractModal({ booking, onClose, onReload }) {
    const [signing, setSigning] = useState(false);
    const [busy, setBusy] = useState(false);

    const openPdf = async () => {
        try {
            const res = await api.get(`/dealer/rental-bookings/${booking.id}/contract.pdf`, { responseType: "blob" });
            previewPdfResponse(res, `Mietvertrag_${booking.contract_number || booking.id.slice(0, 8)}.pdf`, { recipient: customerRecipient(booking.customer) });
        } catch { toast.error("PDF konnte nicht geöffnet werden"); }
    };
    const saveSig = async (dataUrl) => {
        setBusy(true);
        try {
            await api.post(`/dealer/rental-bookings/${booking.id}/sign`, { signature_data_url: dataUrl, role: "pickup" });
            toast.success("Unterschrift gespeichert");
            setSigning(false);
            onReload();
        } catch { toast.error("Unterschrift konnte nicht gespeichert werden"); }
        finally { setBusy(false); }
    };
    const sendMail = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/dealer/rental-bookings/${booking.id}/send-contract`);
            toast.success(`Vertrag an ${data.email} gesendet (Demo/gemockt)`);
        } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    return (
        <Modal title={`Mietvertrag ${booking.contract_number || ""}`} onClose={onClose} wide testid="rental-contract-modal">
            <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-sm space-y-1">
                    <div><strong>{booking.vehicle_snapshot?.manufacturer} {booking.vehicle_snapshot?.model}</strong> · {booking.vehicle_snapshot?.license_plate}</div>
                    <div className="text-gray-600">{fmtDate(booking.start_date)} – {fmtDate(booking.end_date)} · {booking.days} Tag(e) · {fmtEur(booking.total_gross)}</div>
                    {booking.customer?.last_name && <div className="text-gray-600">Mieter: {booking.customer.first_name} {booking.customer.last_name}</div>}
                    {booking.signature_pickup_data_url && <div className="text-emerald-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Unterschrieben</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={openPdf} className="rounded-sm bg-[#111827] text-white font-bold" data-testid="rental-contract-pdf-btn"><FileText className="w-4 h-4 mr-1.5" /> Drucken / PDF</Button>
                    <Button variant="outline" onClick={() => setSigning((s) => !s)} className="rounded-sm" data-testid="rental-contract-sign-btn"><PenLine className="w-4 h-4 mr-1.5" /> Auf Tablet unterschreiben</Button>
                    <Button variant="outline" disabled={busy || !booking.customer?.email} onClick={sendMail} className="rounded-sm" data-testid="rental-contract-mail-btn"><Mail className="w-4 h-4 mr-1.5" /> Per E-Mail senden</Button>
                </div>
                {signing && (
                    <div>
                        <p className="text-xs text-gray-500 mb-1">Unterschrift des Mieters:</p>
                        <SignaturePad onSave={saveSig} onCancel={() => setSigning(false)} />
                    </div>
                )}
            </div>
        </Modal>
    );
}
